import { z } from 'zod/v4';

const LIST_CERTIFICATIONS_DEFAULT_PAGE_SIZE = 15;
const LIST_CERTIFICATIONS_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/certifications` querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/certification.js`'s
 * `list.querystring`.
 *
 * `sort` is dropped: the legacy `sortingColumnsMapping` for this endpoint is
 * `{}` (empty), so a `sort` value was always accepted and always ignored —
 * results are hard-ordered by `id ASC` regardless (see `list.js`'s sibling
 * `audit-types` for the same pattern).
 */
export const ListCertificationsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_CERTIFICATIONS_MAX_PAGE_SIZE)
    .default(LIST_CERTIFICATIONS_DEFAULT_PAGE_SIZE),
  filter: z.string().max(512).optional(),
});

export type ListCertificationsQuery = z.infer<
  typeof ListCertificationsQuerySchema
>;

/**
 * One row of `GET /risk-analysis/certifications`, ported from the legacy
 * `certifications` response definition
 * (`2025-06/risk-analysis-service/routes/schemas/v3/responses.js`).
 * `description` is nullable — the column has no `NOT NULL` constraint.
 */
export const CertificationRowSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  type: z.string().min(1),
  description: z.string().nullable(),
});

export type CertificationRow = z.infer<typeof CertificationRowSchema>;

/**
 * One row of `GET /api/certifications`: the whole certification catalogue as
 * pickers use it. Ported from the legacy `GET /api-service/v3/certifications`,
 * narrowed to the fields any caller reads. `name` and `type` are nullable
 * columns, so they are nullable here too.
 */
export const CertificationOptionSchema = z.object({
  id: z.string().min(1),
  name: z.string().nullable(),
  type: z.string().nullable(),
});

export type CertificationOption = z.infer<typeof CertificationOptionSchema>;
